import Cookies from 'js-cookie';
import { TOKEN_NAME } from '../../constants';

export class AssistantApiError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

// Accept only text turns. Failed/cancelled responses never become model history.
export function conversationInput(messages, question) {
  const history = messages
    .filter((message) => message.status === 'complete')
    .map(({ role, content }) => ({ role, content: content.slice(0, 12000) }))
    .slice(-18);
  while (
    history.length &&
    history.reduce((sum, message) => sum + message.content.length, 0) + question.length > 30000
  ) {
    history.splice(0, 2);
  }
  return [...history, { role: 'user', content: question }];
}

// Operator approval of a chat-proposed action. The token was minted and
// HMAC-bound server-side to the exact tool + arguments + user; the model can
// never call this — only the person clicking the button in the Planner UI.
export async function approveAssistantAction({ baseUrl, token, approval }) {
  if (!token || Cookies.get(TOKEN_NAME) !== token) throw new AssistantApiError('session_expired');
  const response = await fetch(baseUrl.replace(/\/$/, '') + '/assistant/approve', {
    method: 'POST',
    credentials: 'omit',
    redirect: 'error',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ approval }),
  });
  let body;
  try {
    body = await response.json();
  } catch {
    throw new AssistantApiError('assistant_unavailable');
  }
  if (!response.ok || !body.executed) {
    throw new AssistantApiError(body?.error || (response.status === 401 ? 'session_expired' : 'assistant_unavailable'));
  }
  return body;
}

export async function streamAssistant({ baseUrl, token, messages, context, attachment, signal, onEvent }) {
  if (!token || Cookies.get(TOKEN_NAME) !== token) throw new AssistantApiError('session_expired');
  const response = await fetch(baseUrl.replace(/\/$/, '') + '/assistant/chat', {
    method: 'POST',
    credentials: 'omit',
    redirect: 'error',
    signal,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify(attachment ? { messages, context, attachment } : { messages, context }),
  });
  if (!response.ok) {
    let code;
    try {
      code = (await response.json()).error;
    } catch {
      // Proxy errors can be HTML; never render their body.
    }
    throw new AssistantApiError(code || (response.status === 401 ? 'session_expired' : 'assistant_unavailable'));
  }
  if (!response.body || !response.headers.get('content-type')?.includes('application/x-ndjson')) {
    throw new AssistantApiError('incomplete_stream');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completed = false;
  let result;
  const consume = (line) => {
    if (!line.trim()) return;
    if (Cookies.get(TOKEN_NAME) !== token) throw new AssistantApiError('session_expired');
    let event;
    try {
      event = JSON.parse(line);
    } catch {
      throw new AssistantApiError('incomplete_stream');
    }
    if (event.type === 'error') throw new AssistantApiError(event.code);
    if (event.type === 'done') {
      if (typeof event.text !== 'string') throw new AssistantApiError('incomplete_stream');
      completed = true;
      result = event;
    }
    onEvent(event);
  };
  try {
    while (!completed) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      if (buffer.length > 300000) throw new AssistantApiError('response_too_large');
      let end;
      while ((end = buffer.indexOf('\n')) >= 0) {
        consume(buffer.slice(0, end));
        buffer = buffer.slice(end + 1);
      }
    }
    if (!completed) {
      buffer += decoder.decode();
      if (buffer.trim()) consume(buffer);
    }
    if (!completed) throw new AssistantApiError('incomplete_stream');
    return result;
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
